import React, { useState, useEffect } from "react";
import axios from "axios";
import "./Dialer.css";
import { useParams } from "react-router-dom";

const Dialer = () => {
  const [selectedCRM, setSelectedCRM] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [initialApiKey, setInitialApiKey] = useState("");
  const [groups, setGroups] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [fieldList, setFieldList] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [formFields, setFormFields] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [associateMode, setAssociateMode] = useState("pool");
  const [associateList, setAssociateList] = useState([]);
  const [selectedAssociates, setSelectedAssociates] = useState([]);
  const [initialSettings, setInitialSettings] = useState(null);

  const { formId } = useParams();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    axios
      .get(`https://leadbankapi.gensparc.com/api/dialer-settings/${formId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data?.api_key) {
          setSelectedCRM("dialer");
        }
      })
      .catch((error) => {
        console.error("Error checking Dialer connection:", error);
      });
  }, [formId]);

  useEffect(() => {
    if (selectedCRM === "dialer") {
      fetchFormFields();
      fetchDialerDetails();
    }
  }, [selectedCRM]);

  useEffect(() => {
    if (associateMode === "associate" && apiKey) {
      fetchAssociateList();
    }
  }, [associateMode, apiKey]);

  const fetchFormFields = async () => {
    try {
      const response = await axios.get(`https://leadbankapi.gensparc.com/api/fields/dialer/${formId}`);
      setFormFields(response.data);
    } catch (error) {
      console.error("Error fetching form fields:", error);
    }
  };

  const fetchDialerDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      const response = await axios.get(`https://leadbankapi.gensparc.com/api/dialer-settings/${formId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data) {
        const savedApiKey = response.data.api_key || "";
        setApiKey(savedApiKey);
        setInitialApiKey(savedApiKey);
        setSelectedGroup(response.data.group_id || "");
        setSelectedStatus(response.data.status || "");
        setFieldMapping(response.data.field_mapping || {});
        if (response.data.associate_mode === "associate") {
          setAssociateMode("associate");
          const associates = Array.isArray(response.data.associate)
            ? response.data.associate
            : response.data.associate ? [response.data.associate] : [];
          setSelectedAssociates(associates);
        } else if (response.data.associate_mode) {
          setAssociateMode(response.data.associate_mode);
        }

        fetchDialerData(savedApiKey);
        if (response.data.group_id) {
          fetchStatusList(response.data.group_id, savedApiKey);
        }
      }
    } catch (error) {
      console.error("Error fetching Dialer details:", error);
    }
  };

  const fetchDialerData = async (key) => {
    if (!key) return alert("Please enter an API key.");
    try {
      const [groupRes, fieldRes] = await Promise.all([
        axios.get(`https://leadbankapi.gensparc.com/api/fetchGroupList?APIKey=${key}`),
        axios.get(`https://leadbankapi.gensparc.com/api/fetchFieldList?APIKey=${key}`),
      ]);
      if (groupRes.data.ResponseCode === 1) setGroups(groupRes.data.List);
      else alert("Failed to fetch groups.");

      if (fieldRes.data.ResponseCode === 1) {
        const fieldsArray = Object.entries(fieldRes.data.FieldList).map(([key, value]) => ({
          id: key,
          name: value,
        }));
        setFieldList(fieldsArray);
      } else alert("Failed to fetch field list.");
    } catch (error) {
      console.error("Error fetching Dialer data:", error);
    }
  };

  const fetchStatusList = async (groupId, key = apiKey) => {
    if (!key || !groupId) return alert("Please enter an API key and select a group.");
    try {
      const response = await axios.get(
        `https://leadbankapi.gensparc.com/api/fetchStatusList?APIKey=${key}&GroupId=${groupId}`
      );
      if (response.data.ResponseCode === 1) setStatusList(response.data.List);
      else alert("Failed to fetch status list.");
    } catch (error) {
      console.error("Error fetching status list:", error);
    }
  };

  const fetchAssociateList = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || !apiKey) return;

      const res = await axios.get(
        `https://leadbankapi.gensparc.com/api/fetchAssociateList?APIKey=${apiKey}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.ResponseCode === 1 && Array.isArray(res.data.List)) {
        setAssociateList(res.data.List);
      } else {
        alert("No associates found or invalid response");
      }
    } catch (error) {
      console.error("Error fetching associate list:", error);
    }
  };

  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    setSelectedGroup(groupId);
    if (groupId) fetchStatusList(groupId);
  };

  const handleMappingChange = (formField, dialerField) => {
    setFieldMapping((prev) => ({ ...prev, [formField]: dialerField }));
  };

  const handleAssociateChange = (id) => {
    setSelectedAssociates((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const saveDialerSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return alert("Authentication token not found. Please log in again.");
      if (!apiKey || !selectedGroup || !selectedStatus)
        return alert("Please enter an API Key, select a Group, and choose a Status.");

      await axios.post(
        `https://leadbankapi.gensparc.com/api/dialer-settings/${formId}`,
        {
          apiKey,
          groupId: selectedGroup,
          status: selectedStatus,
          fieldMapping,
          Source: "Web Form",
          associate_mode: associateMode,
          associate: associateMode === "associate" ? selectedAssociates : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert("Dialer settings saved successfully!");
    } catch (error) {
      console.error("Error saving Dialer settings:", error);
      alert("Failed to save settings. Please try again.");
    }
  };

  return (
    <div className="dialer-container">
      <h2 className="dialer-title">Select CRM to Connect</h2>
      <div className="dropdown-group">
        <label className="dropdown-label">Connect with:</label>
        <select
          className="dropdown"
          value={selectedCRM}
          onChange={(e) => setSelectedCRM(e.target.value)}
        >
          <option value="">Select CRM</option>
          <option value="dialer">Dialer CRM</option>
          <option value="other">Other CRM</option>
        </select>
      </div>

      {selectedCRM === "dialer" && (
        <>
          <h2 className="dialer-title">Enter API Key & Fetch Data</h2>
          <label className="dropdown-label">API Key:</label>
          <input
            type="text"
            className="input-field"
            placeholder="Enter API Key"
            value={apiKey}
            onChange={(e) => {
              const newApiKey = e.target.value;
              setApiKey(newApiKey);
              if (newApiKey !== initialApiKey) {
                setGroups([]);
                setStatusList([]);
                setSelectedGroup("");
                setSelectedStatus("");
                setFieldList([]);
                setFieldMapping({});
              }
            }}
          />
          {apiKey && apiKey !== initialApiKey && (
            <button className="btn fetch-btn" onClick={() => fetchDialerData(apiKey)}>
              Fetch Groups & Fields
            </button>
          )}

          <div className="dropdown-group">
            <label className="dropdown-label">Group List:</label>
            <select
              className="dropdown"
              value={selectedGroup}
              onChange={handleGroupChange}
            >
              <option value="">Select Group</option>
              {groups.map((group) => (
                <option key={group.GroupId} value={group.GroupId}>
                  {group.Name}
                </option>
              ))}
            </select>
          </div>

          <div className="dropdown-group">
            <label className="dropdown-label">Status List:</label>
            <select
              className="dropdown"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">Select Status</option>
              {statusList.map((status) => (
                <option key={status.Name} value={status.Name}>
                  {status.Name}
                </option>
              ))}
            </select>
          </div>

          <h2 className="dialer-title">Select Assignment Mode</h2>
          <div className="dropdown-group">
            <label>
              <input
                type="radio"
                name="assignType"
                value="pool"
                checked={associateMode === "pool"}
                onChange={() => setAssociateMode("pool")}
              />
              Pool
            </label>
            <label style={{ marginLeft: "1rem" }}>
              <input
                type="radio"
                name="assignType"
                value="associate"
                checked={associateMode === "associate"}
                onChange={() => setAssociateMode("associate")}
              />
              Associate (Round Robin)
            </label>
          </div>

          {associateMode === "associate" && (
            <div className="dropdown-group">
              <label className="dropdown-label">Select Associates:</label>
              {associateList.map((associate) => (
                <div key={associate.UserId}>
                  <label>
                    <input
                      type="checkbox"
                      value={associate.UserId}
                      checked={selectedAssociates.includes(associate.UserId)}
                      onChange={() => handleAssociateChange(associate.UserId)}
                    />
                    {associate.Name}
                  </label>
                </div>
              ))}
            </div>
          )}

          <h2 className="dialer-title">Map Fields</h2>
          <table className="mapping-table">
            <thead>
              <tr>
                <th className="map-form">Form Field</th>
                <th>Dialer Field</th>
              </tr>
            </thead>
            <tbody>
              {formFields.map((formField) => (
                <tr key={formField.id}>
                  <td className="mapped-form">{formField.name}</td>
                  <td>
                    <select
                      className="mapping-dropdown"
                      value={fieldMapping[formField.name] || ""}
                      onChange={(e) =>
                        handleMappingChange(formField.name, e.target.value)
                      }
                    >
                      <option value="">Select Dialer Field</option>
                      {fieldList.map((field) => (
                        <option key={field.id} value={field.name}>
                          {field.name}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="btn save-btn" onClick={saveDialerSettings}>
            Save Dialer Settings
          </button>
        </>
      )}
    </div>
  );
};

export default Dialer;
